import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import Sidebar from '../components/Sidebar';
import ProjectSetupWizard from '../components/ProjectSetupWizard';
import UpgradeModal from '../components/UpgradeModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import PageLoader from '../components/PageLoader';
import './DashboardLayout.css';

const DashboardLayout = ({ children }) => {
  const { user, logout, loading: authLoading } = useAuth();
  const { projects, currentProject, selectProject, deleteProject, userPlan, projectLimit, loading: projectLoading, switchingProject, refreshProjects } = useProject();
  const [showProjectWizard, setShowProjectWizard] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const hasInitialLoadCompleted = useRef(false);
  const navigate = useNavigate();

  // Only show wizard automatically if both auth and projects have finished loading and projects are empty
  useEffect(() => {
    // Wait for both auth and projects to finish loading
    if (!authLoading && !projectLoading) {
      // Only check once after initial load completes
      if (!hasInitialLoadCompleted.current) {
        hasInitialLoadCompleted.current = true;
        // Only show wizard if projects are actually empty AND no current project exists
        // When projectLoading becomes false, React has already batched all state updates
        // so projects and currentProject should be consistent
        if (projects.length === 0 && !currentProject && !showProjectWizard) {
          setShowProjectWizard(true);
        }
      }
    }
  }, [authLoading, projectLoading, projects.length, currentProject, showProjectWizard]);

  // Reset flag when user logs out (user becomes null after being set)
  useEffect(() => {
    if (!user && hasInitialLoadCompleted.current) {
      hasInitialLoadCompleted.current = false;
    }
  }, [user]);

  // Show wizard if projects become empty after deletion (but not during initial load)
  useEffect(() => {
    // Only trigger this after initial load has completed and we're not in the middle of a deletion
    if (hasInitialLoadCompleted.current && !projectLoading && projects.length === 0 && !currentProject && !showProjectWizard && !showDeleteModal) {
      setShowProjectWizard(true);
    }
  }, [projects.length, projectLoading, currentProject, showProjectWizard, showDeleteModal]);

  // Redirect users if they're viewing a project beyond their allowed project_limit
  useEffect(() => {
    if (projectLoading || !projects.length || !currentProject) return;
    
    const effectiveLimit = typeof projectLimit === 'number' ? projectLimit : null;
    
    if (effectiveLimit !== null && projects.length > effectiveLimit) {
      // Find the index of current project
      const currentProjectIndex = projects.findIndex(p => p.id === currentProject.id);
      
      // If current project is beyond the limit, redirect to first project
      if (currentProjectIndex >= effectiveLimit) {
        const firstProject = projects[0];
        if (firstProject && firstProject.id !== currentProject.id) {
          selectProject(firstProject);
        }
      }
    }
  }, [projects, currentProject, projectLimit, projectLoading, selectProject]);

  const handleLogout = () => {
    logout();
    navigate('/signin');
  };

  const handleProjectWizardComplete = async (project) => {
    // Project is already created by ProjectSetupWizard via API
    // ProjectContext.createProject already added it to the projects list
    // No need to call createProject again - that would create a duplicate!
    
    // Just refresh the projects list to ensure we have the latest data
    if (refreshProjects && typeof refreshProjects === 'function') {
      try {
        await refreshProjects();
      } catch (err) {
        console.error('Error refreshing projects:', err);
      }
    }
    
    setShowProjectWizard(false);
    // Don't navigate if we're already on a dashboard page
    if (!window.location.pathname.startsWith('/dashboard')) {
      navigate('/dashboard');
    }
  };

  // Helper function to get project limit based on plan (matches backend logic)
  const getProjectLimit = (plan) => {
    const normalizedPlan = (plan || 'basic').toLowerCase();
    
    // Lifetime plan has unlimited projects
    if (normalizedPlan === 'lifetime') {
      return null; // null means unlimited
    }
    
    // Standard plan limits
    const planLimits = {
      'basic': 1,
      'individual': 1,
      'business': 10,
      'enterprise': 50
    };
    
    return planLimits[normalizedPlan] || 1; // Default to 1 if plan not recognized
  };

  const handleCreateProject = () => {
    const normalizedPlan = (userPlan || 'basic').toLowerCase();
    const projectLimit = getProjectLimit(userPlan);
    
    // Check if user has reached their project limit
    // Lifetime plan (null limit) and custom plan are handled by backend
    if (projectLimit !== null) {
      if (projects.length >= projectLimit) {
        // Show upgrade modal for basic and individual plans
        if (normalizedPlan === 'basic' || normalizedPlan === 'individual') {
          setShowUpgradeModal(true);
          return;
        }
        // For business and enterprise, backend will handle the error message
        // But we can still show upgrade modal as a courtesy
        if (normalizedPlan === 'business' || normalizedPlan === 'enterprise') {
          setShowUpgradeModal(true);
          return;
        }
      }
    }
    
    // For lifetime and custom plans, or if under limit, proceed to wizard
    // Backend will validate custom plan limits
    setShowProjectWizard(true);
  };

  const handleUpgrade = () => {
    setShowUpgradeModal(false);
    // Open pricing page in new tab
    window.open('https://parrotseo.com/pricing', '_blank', 'noopener,noreferrer');
  };

  const handleDeleteProjectClick = (project) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const handleDeleteProjectConfirm = async () => {
    if (!projectToDelete || isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteProject(projectToDelete.id);
      setShowDeleteModal(false);
      setProjectToDelete(null);
      setIsDeleting(false);
      
      // Check if user has no projects left after deletion
      // We need to wait a bit for the projects state to update
      setTimeout(() => {
        // Refresh projects to get updated list
        // The ProjectContext should update automatically, but we'll check
        if (projects.length <= 1) {
          // If this was the last project, show wizard
          setShowProjectWizard(true);
        } else if (currentProject?.id === projectToDelete.id) {
          // If we deleted the current project, navigate to dashboard
          navigate('/dashboard');
        }
      }, 100);
    } catch (error) {
      console.error('Error deleting project:', error);
      alert(`Failed to delete project: ${error.message || 'Unknown error'}`);
      setIsDeleting(false);
      setShowDeleteModal(false);
      setProjectToDelete(null);
    }
  };

  // Show loading screen while auth or projects are loading
  // This ensures we've checked Supabase before deciding to show wizard
  if (authLoading || projectLoading) {
    return <PageLoader forceShow={true} />;
  }

  // Show project wizard only if explicitly requested OR if no projects exist after loading completes
  // Don't show while loading or if projects already exist
  if (showProjectWizard) {
    return (
      <div className="dashboard-layout-full">
        <ProjectSetupWizard 
          onComplete={handleProjectWizardComplete}
          onCancel={() => setShowProjectWizard(false)}
        />
      </div>
    );
  }

  return (
    <>
      {switchingProject && <PageLoader forceShow={true} />}
      <div className="dashboard-layout">
        <Sidebar
          projects={projects}
          currentProject={currentProject}
          onProjectChange={selectProject}
          onCreateProject={handleCreateProject}
          onDeleteProject={handleDeleteProjectClick}
          onLogout={handleLogout}
          userPlan={userPlan}
          projectLimit={projectLimit}
          onShowUpgradeModal={() => setShowUpgradeModal(true)}
        />
        <div className="dashboard-main">
          <div className="dashboard-content">
            {React.cloneElement(children, { user, onLogout: handleLogout, currentProject })}
          </div>
        </div>
      </div>
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
      />
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteModal(false);
            setProjectToDelete(null);
          }
        }}
        onConfirm={handleDeleteProjectConfirm}
        projectName={projectToDelete?.name || ''}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default DashboardLayout;

